import type { CopilotSession } from '@customerio/hydra-copilot';
import { generateCopilotSuggestions } from '@customerio/hydra-copilot';
import { useCopilotMessages, useCopilotStatus } from '@customerio/hydra-copilot/react';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { getInitialStaticSuggestions } from './static-suggestions';

const INACTIVITY_TIMEOUT = 1_000;
const MAX_SUGGESTIONS = 3;
const SUGGESTIONS_STALE_TIME = 5 * 60 * 1000;

export interface SuggestionState {
  suggestions: Array<{ label: string; message: string }>;
  isVisible: boolean;
  isLoading: boolean;
}

export function useSuggestions(session: CopilotSession): SuggestionState {
  const [isVisible, setIsVisible] = useState(false);
  const inactivityTimerRef = useRef<number | undefined>(undefined);
  const status = useCopilotStatus(session);
  const messages = useCopilotMessages(session);
  const pathname = useLocation().pathname;
  const messagePathName = useRef<string | null>(null);
  const messageCountRef = useRef<number>(0);
  const sessionRef = useRef<CopilotSession | null>(session);
  const refreshInstructions = useStore(session.__suggestionsRefreshTrigger);
  const { account } = useAuth();
  const isBuilder = isBuilderPlan(account);

  // Generate static suggestions based on the current route, re-generating when the path changes.
  const staticSuggestions = useMemo(() => getInitialStaticSuggestions(pathname, { isBuilder }), [pathname, isBuilder]);

  const {
    data: dynamicSuggestions = [],
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ['copilotInputSuggestions', session.id],
    queryFn: async () => {
      const result = await generateCopilotSuggestions({
        session,
        maxSuggestions: MAX_SUGGESTIONS,
        instructions: [
          {
            instruction: `
                Suggest what the user should ask next based on the chat history.
                Make these suggestions contextual and helpful for continuing the chat.
                Be sure to use the current page context to inform your suggestions.

                For example, if the user is on the campaigns page, suggest questions related to campaigns.
                If they are on the design studio page, suggest questions related to design studio.

                Your primary job is helping the user understand customer.io, specifcally on the page they are currently viewing.

                All of your suggestions should be phrased as questions and they must trigger lead to a search of the documentation.
              `,
          },
          ...refreshInstructions,
        ],
      });

      // Clear refresh instructions after using them.
      // We can't use the .set() method on the atom because it will trigger the useEffect again.
      refreshInstructions.length = 0;

      return result;
    },
    enabled: false,
    staleTime: SUGGESTIONS_STALE_TIME,
    gcTime: SUGGESTIONS_STALE_TIME,
  });

  // Use static suggestions when:
  // - There are no messages
  // - The suggestions have not been manually refreshed
  // - There are no dynamic suggestions
  const shouldUseStaticSuggestions =
    messages.length === 0 && refreshInstructions.length === 0 && dynamicSuggestions.length === 0;

  // Choose between static and dynamic suggestions
  const suggestions = shouldUseStaticSuggestions ? staticSuggestions : dynamicSuggestions;

  useEffect(
    function refetchSuggestions() {
      const hasReceivedNewMessages = messages.length > messageCountRef.current;
      const hasPathChanged = messagePathName.current !== pathname;
      const hasSessionChanged = sessionRef.current !== session;

      if (hasReceivedNewMessages || hasPathChanged || hasSessionChanged) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
        setIsVisible(false);
        messageCountRef.current = messages.length;
        messagePathName.current = pathname;
        sessionRef.current = session;
      }

      // Show suggestions after timeout when ready/idle
      if (status === 'ready' || status === 'idle') {
        inactivityTimerRef.current = window.setTimeout(async () => {
          if (!shouldUseStaticSuggestions) {
            await refetch();
          }
          setIsVisible(true);
        }, INACTIVITY_TIMEOUT);
      }

      return () => {
        clearTimeout(inactivityTimerRef.current);
      };
    },
    [
      status,
      messages.length,
      refetch,
      pathname,
      session,
      shouldUseStaticSuggestions,
      refreshInstructions,
      staticSuggestions,
    ],
  );

  const areVisible = isVisible && suggestions.length > 0;

  return {
    suggestions,
    isVisible: areVisible,
    isLoading: shouldUseStaticSuggestions ? false : isLoading,
  };
}
